import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { plainTextToDoc, type RichTextDoc } from '@avslack/shared';
import { ApiRequestError, reportOfflineCall, sendMessage } from '../lib/api';
import { useRealtime } from '../realtime/RealtimeProvider';
import { takeServedFromCache } from '../lib/offline-cache';
import {
  mergeMessages,
  messagesKey,
  setChannelMessages,
  type ChannelMessages,
  type LocalMessage,
} from './messages';

/**
 * KIMENO SOR - terero nelkul irt uzenetek (versenyaudit K2, 4. csomag).
 *
 * Eddig a kapcsolat nelkul elkuldott uzenet "Nem sikerult elkuldeni" jelzest
 * kapott, es csak kezzel lehetett ujrakuldeni - ha kozben az app bezarult
 * (telefonon ez gyakori), az uzenet nyom nelkul eltunt.
 *
 * Most a HALOZATI hiba miatt elakadt uzenet az eszkozon marad (localStorage),
 * es MAGATOL elmegy, amint ujra van kapcsolat - ujrainditas utan is. A
 * szerver a `clientMsgId` alapjan szuri a duplikatumot, igy a tobbszori
 * probalkozas sem okoz dupla uzenetet.
 *
 * A SZERVER altal elutasitott uzenet (jogosultsag, archivalt csatorna) NEM
 * kerul ide: azt ujraprobalni ertelmetlen, a felhasznalonak kell donteni.
 */

const STORAGE_KEY = 'avslack:outbox';
/** Ennyi ido utan probaljuk ujra, ha addig semmi nem jelzett kapcsolatot. */
const RETRY_EVERY_MS = 20_000;
/** A jelzes, amit a varakozo uzenet alatt latunk. */
export const QUEUED_REASON = 'Nincs kapcsolat – elküldjük, amint lesz.';

export interface OutboxEntry {
  channelId: string;
  clientMsgId: string;
  authorId: string;
  parentId: string | null;
  text: string;
  body?: RichTextDoc;
  fileIds: string[];
  isImportant: boolean;
  isTask?: boolean;
  createdAt: string;
}

function load(): OutboxEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as OutboxEntry[]) : [];
  } catch {
    return [];
  }
}

function save(entries: OutboxEntry[]): void {
  try {
    if (entries.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Privat mod vagy tele a tarhely: a sor ebben a munkamenetben is mukodik.
  }
}

export function addToOutbox(entry: OutboxEntry): void {
  save([...load().filter((e) => e.clientMsgId !== entry.clientMsgId), entry]);
}

/** Kijelentkezeskor: mas nevevel nem mehet el semmi. */
export function clearOutbox(): void {
  save([]);
  saveCalls([]);
}

// ─── Kapcsolat nelkul elmaradt hivasok ────────────────────────────────────────

/**
 * Hivas, ami kapcsolat hianyaban el sem indult (K2). Amint ujra van
 * kapcsolat, a hivott "Nem fogadott hivas" jelzest kap rola - kulonben sosem
 * tudna meg, hogy kerestek. A szerver dobja el, ha mar ket oranal regebbi.
 */
export interface OfflineCall {
  channelId: string;
  clientId: string;
  authorId: string;
  attemptedAt: string;
}

const CALLS_KEY = 'avslack:outbox-calls';

function loadCalls(): OfflineCall[] {
  try {
    const raw = localStorage.getItem(CALLS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as OfflineCall[]) : [];
  } catch {
    return [];
  }
}

function saveCalls(calls: OfflineCall[]): void {
  try {
    if (calls.length === 0) localStorage.removeItem(CALLS_KEY);
    else localStorage.setItem(CALLS_KEY, JSON.stringify(calls));
  } catch {
    // privat mod: ebben a munkamenetben mar nem tudjuk megjegyezni
  }
}

export function queueOfflineCall(call: OfflineCall): void {
  // Ugyanabba a beszelgetesbe egymas utan tobbszor nyomott hivas EGY jelzes.
  saveCalls([...loadCalls().filter((c) => c.channelId !== call.channelId), call]);
}

function removeCall(clientId: string): void {
  saveCalls(loadCalls().filter((c) => c.clientId !== clientId));
}

export function removeFromOutbox(clientMsgId: string): void {
  const entries = load();
  if (entries.some((e) => e.clientMsgId === clientMsgId)) {
    save(entries.filter((e) => e.clientMsgId !== clientMsgId));
  }
}

/**
 * A szerver NEM ERHETO EL: nincs halozat, vagy az atjaro jelzi, hogy mogotte
 * nem fut (502/503/504). Egy 500-as hiba NEM ilyen - az a szerver valasza.
 */
export function isUnreachable(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return error instanceof TypeError;
  return error.status === 502 || error.status === 503 || error.status === 504;
}

/** Halozati (vagy atmeneti szerver-) hiba - ujraprobalhato. */
export function isRetryableFailure(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  return error.status >= 500 || error.status === 408 || error.status === 429;
}

/** A varakozo uzenet a listaban - ugyanugy nez ki, mint egy elakadt kuldes. */
function queuedMessage(entry: OutboxEntry): LocalMessage {
  const body = entry.text ? (entry.body ?? plainTextToDoc(entry.text)) : null;
  return {
    id: `pending-${entry.clientMsgId}`,
    channelId: entry.channelId,
    seq: Number.MAX_SAFE_INTEGER,
    userId: entry.authorId,
    parentId: entry.parentId,
    text: entry.text,
    body,
    subtype: 'standard',
    meta: null,
    clientMsgId: entry.clientMsgId,
    pendingFileIds: entry.fileIds,
    createdAt: entry.createdAt,
    editedAt: null,
    deletedAt: null,
    reactions: [],
    files: [],
    links: [],
    isPinned: false,
    isImportant: entry.isImportant,
    task: entry.isTask ? { done: false, doneBy: null, doneAt: null } : null,
    isSaved: false,
    threadReplyCount: 0,
    threadLastReplyAt: null,
    pending: true,
    failed: true,
    failedReason: QUEUED_REASON,
  };
}

/** A betoltott csatornaba visszatesszuk a meg el nem ment uzeneteket (ujrainditas utan). */
function showQueued(queryClient: QueryClient, channelId: string): void {
  const queued = load().filter((e) => e.channelId === channelId);
  if (queued.length === 0) return;
  const data = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
  if (!data) return;
  const present = new Set(data.messages.map((m) => m.clientMsgId));
  const missing = queued.filter((e) => !present.has(e.clientMsgId));
  if (missing.length === 0) return;
  setChannelMessages(queryClient, channelId, (current) => ({
    ...current,
    messages: mergeMessages(current.messages, missing.map(queuedMessage)),
  }));
}

/** A terero nelkul nyitott csatornak (helyorzo) ujratoltese - most mar betolthetok. */
function reloadOfflineChannels(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => (query.state.data as ChannelMessages | undefined)?.offline === true,
  });
}

let flushing = false;

async function flush(queryClient: QueryClient, meId: string): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    // Csak a SAJAT uzeneteink: kozos eszkozon a masik kolléga sora nem mehet el a mi nevunkben.
    for (const entry of load().filter((e) => e.authorId === meId)) {
      try {
        const result = await sendMessage(entry.channelId, {
          text: entry.text,
          clientMsgId: entry.clientMsgId,
          parentId: entry.parentId,
          ...(entry.body && entry.text ? { body: entry.body } : {}),
          ...(entry.fileIds.length > 0 ? { fileIds: entry.fileIds } : {}),
          ...(entry.isImportant ? { isImportant: true } : {}),
          ...(entry.isTask ? { isTask: true } : {}),
        });
        removeFromOutbox(entry.clientMsgId);
        setChannelMessages(queryClient, entry.channelId, (data) => ({
          ...data,
          messages: mergeMessages(
            data.messages.filter((m) => m.clientMsgId !== entry.clientMsgId || !m.pending),
            [result.message],
          ),
          channelLastSeq: Math.max(data.channelLastSeq, result.message.seq),
        }));
      } catch (error) {
        if (isRetryableFailure(error)) return; // meg mindig nincs kapcsolat: kesobb
        // A szerver elutasitotta: kivesszuk a sorbol, a felhasznalo dont.
        removeFromOutbox(entry.clientMsgId);
        const reason = error instanceof Error ? error.message : 'Nem sikerült elküldeni.';
        setChannelMessages(queryClient, entry.channelId, (data) => ({
          ...data,
          messages: data.messages.map((m) =>
            m.clientMsgId === entry.clientMsgId && m.pending
              ? { ...m, failed: true, failedReason: reason }
              : m,
          ),
        }));
      }
    }
    for (const call of loadCalls().filter((c) => c.authorId === meId)) {
      try {
        const result = await reportOfflineCall(call.channelId, {
          clientId: call.clientId,
          attemptedAt: call.attemptedAt,
        });
        removeCall(call.clientId);
        // A hivo sajat listajaban is rogton latszik - nem var a valos ideju kapcsolatra.
        const message = result.message;
        if (message) {
          setChannelMessages(queryClient, call.channelId, (data) => ({
            ...data,
            messages: mergeMessages(data.messages, [message]),
            channelLastSeq: Math.max(data.channelLastSeq, message.seq),
          }));
        }
      } catch (error) {
        if (isRetryableFailure(error)) return;
        removeCall(call.clientId);
      }
    }
  } finally {
    flushing = false;
  }
}

/**
 * A sor kiuritese: indulaskor, amikor a bongeszo halozatot jelez, amikor a
 * valos ideju kapcsolat helyreall, es biztonsagbol idonkent.
 */
export function useOutboxFlusher(meId: string): void {
  const queryClient = useQueryClient();
  const { status } = useRealtime();

  useEffect(() => {
    if (status !== 'online') return;
    reloadOfflineChannels(queryClient);
    // A kapcsolat nelkul, az eszkozrol betoltott adatok (belepes, csatornak, kollegak) frissitese.
    for (const name of takeServedFromCache()) {
      void queryClient.invalidateQueries({ queryKey: [name] });
    }
    void flush(queryClient, meId);
  }, [status, queryClient, meId]);

  useEffect(() => {
    const run = () => void flush(queryClient, meId);
    window.addEventListener('online', run);
    const timer = window.setInterval(() => {
      if (load().length > 0 || loadCalls().length > 0) run();
      // Ha csak a szerver akadt meg (a valos ideju kapcsolat el), innen jon vissza a lista.
      reloadOfflineChannels(queryClient);
    }, RETRY_EVERY_MS);

    // Ha egy csatorna betoltodik, a hozza tartozo varakozo uzenetek latszanak.
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = event.query.queryKey;
      if (event.type !== 'updated' || key[0] !== 'messages' || typeof key[1] !== 'string') return;
      showQueued(queryClient, key[1]);
    });

    run();
    return () => {
      window.removeEventListener('online', run);
      window.clearInterval(timer);
      unsubscribe();
    };
  }, [queryClient, meId]);
}

/** A varakozo uzenet elvetese: kikerul a sorbol es a listabol is. */
export function discardQueued(queryClient: QueryClient, message: LocalMessage): void {
  if (!message.clientMsgId) return;
  removeFromOutbox(message.clientMsgId);
  setChannelMessages(queryClient, message.channelId, (data) => ({
    ...data,
    messages: data.messages.filter((m) => m.id !== message.id),
  }));
}
